/**
 * All interaction state for the map tool overlay.
 *
 * Same shape as the controller AreaPicker already uses (`addVertex` / `applyCustom`),
 * just as a hook, so the pattern reads as native to this codebase. WeatherMap keeps
 * none of this: it receives finished GeoJSON and hands back raw clicks.
 *
 * Drawn zones are deliberately client-side. The persisted, worker-evaluated,
 * SMS-backed geofence already exists — it is an Asset with an alertRadiusMiles — and
 * a second one would give two different answers to "am I affected". A finished
 * circle can be promoted to a real Asset instead; see MapTools.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  echoNear,
  haversineMiles,
  stormSwath,
  type AreaGeometry,
  type StormMotion,
  type TenantAlert,
} from '@ww/shared';
import type { MapToolsState } from '../components/WeatherMap';
import {
  EMPTY_TOOL_STATE,
  primaryAlert,
  renderTools,
  type LngLat,
  type ToolMode,
  type ToolState,
} from '../lib/mapTools';

const HINTS: Record<ToolMode, string> = {
  none: '',
  eta: 'Tap a spot to see when weather reaches it — then drag the dot to aim a track by hand',
  'storm-path': 'Tap a warning, or an area of rain, to project where it could go',
  measure: 'Tap two points to measure',
  'zone-circle': 'Tap the centre, then tap again to set the radius',
  'zone-polygon': 'Tap to add corners — three or more make a zone',
  markup: 'Drag to draw. The map will not pan while you do.',
};

const STORAGE_KEY = (tenantId: string) => `ww.tools.${tenantId}`;

function loadPersisted(tenantId: string | null | undefined): Pick<ToolState, 'zones' | 'strokes'> | null {
  if (!tenantId) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY(tenantId));
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<ToolState>;
    return { zones: p.zones ?? [], strokes: p.strokes ?? [] };
  } catch {
    return null;
  }
}

let seq = 0;
const nextId = () => `t${Date.now().toString(36)}${(seq++).toString(36)}`;

export interface UseMapDraw {
  state: ToolState;
  setMode: (m: ToolMode) => void;
  undo: () => void;
  clear: () => void;
  removeZone: (id: string) => void;
  /** Close the polygon currently being drawn into a finished zone. */
  commitPolygon: () => void;
  /** Aim the arrival cone by hand, in compass degrees TOWARD. */
  setManualBearing: (deg: number | null) => void;
  /** Ready to spread onto <WeatherMap tools={...} />. */
  toolsProp: MapToolsState;
  pinEta: ReturnType<typeof renderTools>['pinEta'];
  pinEtaManual: ReturnType<typeof renderTools>['pinEtaManual'];
  /** The storm the user tapped, or why nothing could be projected from it. */
  pickedPath: PickedPath | null;
  measureText: string | null;
  zoneEtas: ReturnType<typeof renderTools>['zoneEtas'];
  /** The alert the readouts are reasoning about, for naming it in the panel. */
  subject: { event: string } | null;
}

export interface PickedPath {
  swath: AreaGeometry | null;
  label: string;
  /** Why nothing is drawn, in the user's words. Null when a path is drawn. */
  refusal: string | null;
  geometry: AreaGeometry | null;
  motion: StormMotion | null;
}

export function useMapDraw(
  tenantId: string | null | undefined,
  alerts: TenantAlert[],
  nowMs: number,
  /** Radar-derived rain, used only where no warning covers the point. */
  rain?: {
    motion: StormMotion | null;
    geometry: AreaGeometry | null;
    reason?: string;
    bbox?: [number, number, number, number];
  } | null,
  /** How far ahead a tapped storm is projected. */
  swathMinutes = 60,
): UseMapDraw {
  const [state, setState] = useState<ToolState>(() => ({ ...EMPTY_TOOL_STATE, ...(loadPersisted(tenantId) ?? {}) }));

  // Reload when the acting tenant changes; zones belong to a place.
  useEffect(() => {
    setState({ ...EMPTY_TOOL_STATE, ...(loadPersisted(tenantId) ?? {}) });
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    try {
      window.localStorage.setItem(STORAGE_KEY(tenantId), JSON.stringify({ zones: state.zones, strokes: state.strokes }));
    } catch {
      /* storage unavailable; the drawings still work for this session */
    }
  }, [tenantId, state.zones, state.strokes]);

  const setMode = useCallback((mode: ToolMode) => {
    // Switching tools abandons whatever half-finished shape was in progress,
    // but never the finished ones.
    setState((s) => ({
      ...s,
      mode: s.mode === mode ? 'none' : mode,
      pending: [],
      pathPick: mode === 'storm-path' ? s.pathPick : null,
      measure: mode === 'measure' ? [] : s.measure,
    }));
  }, []);

  const pickPath = useCallback((lon: number, lat: number, hit: { alertId: string | null }) => {
    setState((s) => ({ ...s, pathPick: { point: [lon, lat], alertId: hit.alertId } }));
  }, []);

  const handlePoint = useCallback((lon: number, lat: number) => {
    const p: LngLat = [lon, lat];
    setState((s) => {
      switch (s.mode) {
        case 'storm-path':
          // Handled by pickPath, which also needs to know what was under the cursor.
          return s;
        case 'eta':
          // A hand-aimed track belonged to the old pin; carrying it to a new spot would
          // silently apply a guess the user made about somewhere else.
          return { ...s, pin: p, manualBearing: null };
        case 'measure':
          return { ...s, measure: s.measure.length >= 2 ? [p] : [...s.measure, p] };
        case 'zone-circle': {
          if (s.pending.length === 0) return { ...s, pending: [p] };
          const radiusMiles = haversineMiles(s.pending[0]!, p);
          if (radiusMiles < 0.1) return { ...s, pending: [] };
          return {
            ...s,
            pending: [],
            zones: [...s.zones, { id: nextId(), kind: 'circle', points: [s.pending[0]!], radiusMiles }],
          };
        }
        case 'zone-polygon':
          return { ...s, pending: [...s.pending, p] };
        default:
          return s;
      }
    });
  }, []);

  /** Close the polygon currently being drawn. */
  const commitPolygon = useCallback(() => {
    setState((s) =>
      s.pending.length >= 3
        ? { ...s, pending: [], zones: [...s.zones, { id: nextId(), kind: 'polygon', points: s.pending }] }
        : { ...s, pending: [] },
    );
  }, []);

  const onStroke = useCallback((points: LngLat[]) => {
    setState((s) => ({ ...s, strokes: [...s.strokes, { id: nextId(), points }] }));
  }, []);

  const undo = useCallback(() => {
    setState((s) => {
      if (s.pending.length) return { ...s, pending: s.pending.slice(0, -1) };
      if (s.mode === 'markup' && s.strokes.length) return { ...s, strokes: s.strokes.slice(0, -1) };
      if (s.mode === 'measure' && s.measure.length) return { ...s, measure: s.measure.slice(0, -1) };
      if (s.mode === 'eta' && s.pin) return { ...s, pin: null };
      if (s.zones.length) return { ...s, zones: s.zones.slice(0, -1) };
      if (s.strokes.length) return { ...s, strokes: s.strokes.slice(0, -1) };
      return s;
    });
  }, []);

  const clear = useCallback(() => {
    setState((s) => ({ ...EMPTY_TOOL_STATE, mode: s.mode }));
  }, []);

  const setManualBearing = useCallback((deg: number | null) => {
    setState((s) => ({ ...s, manualBearing: deg === null ? null : ((deg % 360) + 360) % 360 }));
  }, []);

  const removeZone = useCallback((id: string) => {
    setState((s) => ({ ...s, zones: s.zones.filter((z) => z.id !== id) }));
  }, []);

  // Recomputed on the live clock so estimates count down as the storm closes in.
  const now = useMemo(() => new Date(nowMs).toISOString(), [nowMs]);
  const anchor = state.pin ?? state.zones[0]?.points[0] ?? state.measure[0] ?? null;
  const fromAlerts = useMemo(() => primaryAlert(alerts, anchor, now), [alerts, anchor?.[0], anchor?.[1], now]);
  // Warnings win when there is one; otherwise measure against whatever radar shows, so the
  // tools still answer "when does this reach me" on an ordinary wet day.
  const subject = useMemo(() => {
    if (fromAlerts?.motion) return fromAlerts;
    if (rain?.motion && rain.geometry) {
      return { geometry: rain.geometry, motion: rain.motion, event: 'Precipitation' };
    }
    return fromAlerts;
  }, [fromAlerts, rain]);
  const render = useMemo(() => renderTools({ state, alert: subject, now }), [state, subject, now]);

  /**
   * A storm the user tapped. Refuses out loud rather than drawing something meaningless:
   * a watch has no single storm to follow, a warning without a track has no direction,
   * and a vector `etaToPoint` would reject as stale must not still paint a confident
   * path across the county.
   */
  const pickedPath = useMemo<PickedPath | null>(() => {
    const pick = state.pathPick;
    if (!pick) return null;
    const none = (refusal: string): PickedPath => ({ swath: null, label: '', refusal, geometry: null, motion: null });

    const ta = pick.alertId ? alerts.find((a) => a.alert.nwsId === pick.alertId) : null;
    if (pick.alertId && !ta) return none('That warning is no longer active.');

    let geometry: AreaGeometry | null = null;
    let motion: StormMotion | null = null;
    let label = '';
    if (ta) {
      const cat = ta.evaluation?.category ?? ta.alert.category;
      if (cat !== 'warning') return none("A watch covers a whole county — there's no single storm here to follow.");
      if (!ta.alert.motion) return none("This warning doesn't include a storm track, so we can't project where it goes.");
      geometry = ta.alert.geometry;
      motion = ta.alert.motion;
      label = ta.alert.event;
    } else {
      /*
       * `rain` is null only while the nowcast for this spot is still in flight — the
       * anchor follows the tap, so a fresh tap always has a fetch behind it. Saying "no
       * track" during that second would be wrong, and reads exactly like a broken tool.
       */
      /*
       * `rain` describes whichever box was last fetched. When the anchor moves to a new
       * tap there is a window where it still holds the previous area, and using it drew
       * a path over a storm ninety miles from the one that was tapped. Treat data for
       * somewhere else as "not loaded yet".
       */
      const b = rain?.bbox;
      const covers =
        !!b && pick.point[0] >= b[0] && pick.point[0] <= b[2] && pick.point[1] >= b[1] && pick.point[1] <= b[3];
      if (!rain || !covers) return none('Checking radar for a track…');
      if (!rain.geometry || !rain.motion) {
        return none(
          rain.reason === 'no-echo'
            ? 'Radar sees nothing moving here.'
            : rain.reason === 'no-correlation' || rain.reason === 'implausible-speed'
              ? "Radar can't lock onto a track here."
              : 'No track available for this spot.',
        );
      }
      /*
       * Only the echo belonging to the storm that was tapped. The nowcast box is ~90
       * miles across and routinely holds several unrelated storms; projecting all of it
       * takes the leading edge of the whole footprint, which is how a path ended up
       * drawn over a different cell.
       */
      const near = echoNear(rain.geometry, pick.point, 30);
      if (!near) return none('No rain close enough to that spot to follow.');
      geometry = near;
      motion = rain.motion;
      label = 'Precipitation';
    }

    const ageMin = (nowMs - new Date(motion.observedAt).getTime()) / 60_000;
    if (!(ageMin <= 30)) return none('The track we have for this storm is too old to project.');

    const swath = stormSwath(geometry, motion, swathMinutes);
    if (!swath) return none("This storm isn't moving enough to project a path.");
    return { swath, label, refusal: null, geometry, motion };
  }, [state.pathPick, alerts, rain, nowMs, swathMinutes]);

  const toolsProp = useMemo<MapToolsState>(
    () => ({
      active: state.mode !== 'none',
      features:
        pickedPath?.swath
          ? {
              type: 'FeatureCollection',
              features: [
                ...render.features.features,
                { type: 'Feature', geometry: pickedPath.swath as never, properties: { k: 'path-pick' } },
              ],
            }
          : render.features,
      onMapPoint: handlePoint,
      onMapPick: state.mode === 'storm-path' ? pickPath : undefined,
      onStroke,
      freehand: state.mode === 'markup',
      bearingOrigin: state.pin,
      bearingHandle: render.bearingHandle,
      // The handle is upwind, so the direction the user drags it is where weather comes
      // FROM; the motion bearing is the opposite of that.
      onBearingDrag: (deg: number) => setManualBearing(deg + 180),
      hint: state.mode === 'zone-polygon' && state.pending.length >= 3 ? 'Tap "Finish zone" when the shape is right' : HINTS[state.mode],
    }),
    [state.mode, state.pending.length, render.features, render.bearingHandle, state.pin, pickedPath, handlePoint, pickPath, onStroke, setManualBearing],
  );

  return {
    state,
    setMode,
    undo,
    clear,
    removeZone,
    commitPolygon,
    setManualBearing,
    toolsProp,
    pinEta: render.pinEta,
    pinEtaManual: render.pinEtaManual,
    pickedPath,
    measureText: render.measureText,
    zoneEtas: render.zoneEtas,
    subject: subject ? { event: subject.event } : null,
  };
}
